import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { parseServerError } from "utils/errors";
import {
  deleteRegistrationTracker,
  getRegistrationTracker,
  getRegistrationTrackerHistory,
  getRegistrationTrackers,
  getRegistrationTrackerStates,
  getRegistrationTrackerTypes,
  putRegistrationTracker,
  postRegistrationTracker,
} from "./registrationTrackerApi";

function normalizeTrackerList(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload?.items && Array.isArray(payload.items)) return payload.items;
  if (payload?.data && Array.isArray(payload.data)) return payload.data;
  if (payload?.results && Array.isArray(payload.results)) return payload.results;
  return [];
}

function normalizeStates(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload?.items && Array.isArray(payload.items)) return payload.items;
  if (payload?.data && Array.isArray(payload.data)) return payload.data;
  if (payload?.states && Array.isArray(payload.states)) return payload.states;
  return [];
}

function normalizeRegistrationTrackerTypes(payload) {
  const raw = Array.isArray(payload)
    ? payload
    : payload?.items && Array.isArray(payload.items)
      ? payload.items
      : payload?.data && Array.isArray(payload.data)
        ? payload.data
        : payload?.types && Array.isArray(payload.types)
          ? payload.types
          : [];
  return raw
    .filter((entry) => entry != null && typeof entry === "object")
    .map((entry) => {
      const code = String(entry.code ?? "").trim();
      const name = String(entry.name ?? entry.label ?? code).trim();
      const categoriesRaw = Array.isArray(entry.categories) ? entry.categories : [];
      const categories = categoriesRaw
        .filter((c) => c != null && typeof c === "object")
        .map((c) => ({
          code: String(c.code ?? "").trim(),
          name: String(c.name ?? c.label ?? c.code ?? "").trim(),
        }))
        .filter((c) => c.code !== "");
      const operationsRaw = Array.isArray(entry.operation) ? entry.operation : [];
      const operation = operationsRaw
        .filter((op) => op != null && typeof op === "object")
        .map((op) => ({
          code: String(op.code ?? "").trim(),
          name: String(op.name ?? op.label ?? op.code ?? "").trim(),
        }))
        .filter((op) => op.code !== "");
      return { code, name: name || code, categories, operation };
    })
    .filter((entry) => entry.code !== "");
}

function normalizeRegistrationTrackerHistory(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload?.items && Array.isArray(payload.items)) return payload.items;
  if (payload?.data && Array.isArray(payload.data)) return payload.data;
  if (payload?.history && Array.isArray(payload.history)) return payload.history;
  return [];
}

function historyTrackerKey(trackerId) {
  return String(trackerId ?? "").trim();
}

export const fetchRegistrationTrackers = createAsyncThunk(
  "registrationTracker/fetchList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRegistrationTrackers();
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || err.error || "Server error");
      }
      const data = await response.json();
      return normalizeTrackerList(data);
    } catch (error) {
      return rejectWithValue(parseServerError(error));
    }
  }
);

export const fetchRegistrationTrackerHistory = createAsyncThunk(
  "registrationTracker/fetchHistory",
  async (trackerId, { rejectWithValue }) => {
    const key = historyTrackerKey(trackerId);
    if (!key) {
      return rejectWithValue("Tracker id is required");
    }
    try {
      const response = await getRegistrationTrackerHistory(key);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || err.error || "Server error");
      }
      const data = await response.json();
      return {
        trackerId: key,
        items: normalizeRegistrationTrackerHistory(data),
      };
    } catch (error) {
      return rejectWithValue(parseServerError(error));
    }
  }
);

export const fetchRegistrationTrackerDetail = createAsyncThunk(
  "registrationTracker/fetchDetail",
  async (trackerId, { rejectWithValue }) => {
    try {
      const response = await getRegistrationTracker(trackerId);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || err.error || "Server error");
      }
      return await response.json();
    } catch (error) {
      return rejectWithValue(parseServerError(error));
    }
  }
);

export const createRegistrationTracker = createAsyncThunk(
  "registrationTracker/create",
  async (body, { rejectWithValue }) => {
    try {
      const response = await postRegistrationTracker(body);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || err.error || "Server error");
      }
      return await response.json();
    } catch (error) {
      return rejectWithValue(parseServerError(error));
    }
  }
);

export const updateRegistrationTracker = createAsyncThunk(
  "registrationTracker/update",
  async ({ trackerId, body }, { rejectWithValue }) => {
    try {
      const response = await putRegistrationTracker(trackerId, body);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || err.error || "Server error");
      }
      return await response.json();
    } catch (error) {
      return rejectWithValue(parseServerError(error));
    }
  }
);

export const removeRegistrationTracker = createAsyncThunk(
  "registrationTracker/delete",
  async (trackerId, { rejectWithValue }) => {
    try {
      const response = await deleteRegistrationTracker(trackerId);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || err.error || "Server error");
      }
      return trackerId;
    } catch (error) {
      return rejectWithValue(parseServerError(error));
    }
  }
);

export const fetchRegistrationTrackerTypes = createAsyncThunk(
  "registrationTracker/fetchTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRegistrationTrackerTypes();
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || err.error || "Server error");
      }
      const data = await response.json();
      return normalizeRegistrationTrackerTypes(data);
    } catch (error) {
      return rejectWithValue(parseServerError(error));
    }
  }
);

export const fetchRegistrationTrackerStates = createAsyncThunk(
  "registrationTracker/fetchStates",
  async (countryId, { rejectWithValue }) => {
    const normalized = String(countryId ?? "")
      .trim()
      .toUpperCase();
    if (!normalized) {
      return rejectWithValue("Country is required");
    }
    try {
      const response = await getRegistrationTrackerStates(normalized);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || err.error || "Server error");
      }
      const data = await response.json();
      return { countryId: normalized, states: normalizeStates(data) };
    } catch (error) {
      return rejectWithValue(parseServerError(error));
    }
  }
);

const initialState = {
  list: [],
  listLoading: false,
  listError: null,
  detail: null,
  detailLoading: false,
  saving: false,
  saveError: null,
  deletingId: null,
  deleteError: null,
  statesByCountry: {},
  statesLoadingCountry: null,
  statesError: null,
  types: [],
  typesLoading: false,
  typesError: null,
  historyByTrackerId: {},
  historyLoadingByTrackerId: {},
  historyErrorByTrackerId: {},
};

const registrationTrackerSlice = createSlice({
  name: "registrationTracker",
  initialState,
  reducers: {
    clearRegistrationTrackerDetail: (state) => {
      state.detail = null;
      state.detailLoading = false;
    },
    clearRegistrationTrackerErrors: (state) => {
      state.listError = null;
      state.saveError = null;
      state.deleteError = null;
      state.statesError = null;
      state.typesError = null;
    },
    clearRegistrationTrackerTypesError: (state) => {
      state.typesError = null;
    },
    clearRegistrationTrackerStatesError: (state) => {
      state.statesError = null;
    },
    clearRegistrationTrackerStatesCache: (state) => {
      state.statesByCountry = {};
      state.statesLoadingCountry = null;
      state.statesError = null;
    },
    clearRegistrationTrackerHistoryError: (state, action) => {
      const key = historyTrackerKey(action.payload);
      if (!key) return;
      delete state.historyErrorByTrackerId[key];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRegistrationTrackers.pending, (state) => {
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(fetchRegistrationTrackers.fulfilled, (state, action) => {
        state.listLoading = false;
        state.list = action.payload;
      })
      .addCase(fetchRegistrationTrackers.rejected, (state, action) => {
        state.listLoading = false;
        state.list = [];
        state.listError = action.payload || action.error?.message;
      });

    builder
      .addCase(fetchRegistrationTrackerDetail.pending, (state) => {
        state.detailLoading = true;
      })
      .addCase(fetchRegistrationTrackerDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.detail = action.payload;
      })
      .addCase(fetchRegistrationTrackerDetail.rejected, (state) => {
        state.detailLoading = false;
        state.detail = null;
      });

    builder
      .addCase(createRegistrationTracker.pending, (state) => {
        state.saving = true;
        state.saveError = null;
      })
      .addCase(createRegistrationTracker.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(createRegistrationTracker.rejected, (state, action) => {
        state.saving = false;
        state.saveError = action.payload || action.error?.message;
      });

    builder
      .addCase(updateRegistrationTracker.pending, (state) => {
        state.saving = true;
        state.saveError = null;
      })
      .addCase(updateRegistrationTracker.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(updateRegistrationTracker.rejected, (state, action) => {
        state.saving = false;
        state.saveError = action.payload || action.error?.message;
      });

    builder
      .addCase(removeRegistrationTracker.pending, (state, action) => {
        state.deletingId = action.meta.arg;
        state.deleteError = null;
      })
      .addCase(removeRegistrationTracker.fulfilled, (state, action) => {
        state.deletingId = null;
        const id = action.payload;
        state.list = state.list.filter((row) => String(row.id) !== String(id));
      })
      .addCase(removeRegistrationTracker.rejected, (state, action) => {
        state.deletingId = null;
        state.deleteError = action.payload || action.error?.message;
      });

    builder
      .addCase(fetchRegistrationTrackerTypes.pending, (state) => {
        state.typesLoading = true;
        state.typesError = null;
      })
      .addCase(fetchRegistrationTrackerTypes.fulfilled, (state, action) => {
        state.typesLoading = false;
        state.types = action.payload;
      })
      .addCase(fetchRegistrationTrackerTypes.rejected, (state, action) => {
        state.typesLoading = false;
        state.typesError = action.payload || action.error?.message;
      });

    builder
      .addCase(fetchRegistrationTrackerStates.pending, (state, action) => {
        const key = String(action.meta.arg ?? "")
          .trim()
          .toUpperCase();
        state.statesLoadingCountry = key || action.meta.arg;
        state.statesError = null;
      })
      .addCase(fetchRegistrationTrackerStates.fulfilled, (state, action) => {
        state.statesLoadingCountry = null;
        const { countryId, states } = action.payload;
        state.statesByCountry[countryId] = states;
      })
      .addCase(fetchRegistrationTrackerStates.rejected, (state, action) => {
        state.statesLoadingCountry = null;
        state.statesError = action.payload || action.error?.message;
        const key = String(action.meta.arg ?? "")
          .trim()
          .toUpperCase();
        if (key && !Object.prototype.hasOwnProperty.call(state.statesByCountry, key)) {
          state.statesByCountry[key] = [];
        }
      });

    builder
      .addCase(fetchRegistrationTrackerHistory.pending, (state, action) => {
        const key = historyTrackerKey(action.meta.arg);
        if (!key) return;
        state.historyLoadingByTrackerId[key] = true;
        delete state.historyErrorByTrackerId[key];
      })
      .addCase(fetchRegistrationTrackerHistory.fulfilled, (state, action) => {
        const { trackerId, items } = action.payload;
        state.historyLoadingByTrackerId[trackerId] = false;
        state.historyByTrackerId[trackerId] = items;
      })
      .addCase(fetchRegistrationTrackerHistory.rejected, (state, action) => {
        const key = historyTrackerKey(action.meta.arg);
        if (!key) return;
        state.historyLoadingByTrackerId[key] = false;
        state.historyErrorByTrackerId[key] =
          action.payload || action.error?.message;
      });
  },
});

export const {
  clearRegistrationTrackerDetail,
  clearRegistrationTrackerErrors,
  clearRegistrationTrackerTypesError,
  clearRegistrationTrackerStatesError,
  clearRegistrationTrackerStatesCache,
  clearRegistrationTrackerHistoryError,
} = registrationTrackerSlice.actions;

export default registrationTrackerSlice.reducer;
